import type { TranscriptEvent } from '@/api/types'

export interface QuestionChoice {
  label: string
  value: string
}

export interface QuestionnaireQuestion {
  id: string
  question: string
  choices: QuestionChoice[]
  allow_free_text: boolean
  header: string
}

export interface PendingQuestionnaire {
  status: 'waiting'
  interaction: 'questionnaire'
  mode: 'single' | 'bulk'
  questions: QuestionnaireQuestion[]
}

export interface QuestionnaireAnswer {
  id: string
  header: string
  question: string
  answer: string
}

export interface QuestionnaireAnswerBundle {
  type: 'questionnaire_answers'
  questionnaire: PendingQuestionnaire
  answers: QuestionnaireAnswer[]
}

const QUESTION_TOOL_NAMES = new Set([
  'mcp__shajgoj-products__ask_user_question',
  'mcp__shajgoj-products__ask_user_questions',
])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function normalizeChoices(value: unknown): QuestionChoice[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) => {
    if (!isRecord(item)) return []
    const label = typeof item.label === 'string' ? item.label.trim() : ''
    const choiceValue = typeof item.value === 'string' ? item.value.trim() : ''
    if (!label || !choiceValue) return []
    return [{ label, value: choiceValue }]
  })
}

function normalizeQuestion(
  value: unknown,
  index: number,
): QuestionnaireQuestion | null {
  if (!isRecord(value)) return null
  const question = typeof value.question === 'string' ? value.question.trim() : ''
  const choices = normalizeChoices(value.choices)
  if (!question || choices.length === 0) return null
  const id = typeof value.id === 'string' && value.id.trim()
    ? value.id.trim()
    : `q${index + 1}`
  return {
    id,
    question,
    choices,
    allow_free_text: Boolean(value.allow_free_text),
    header: typeof value.header === 'string' ? value.header.trim() : '',
  }
}

export function isQuestionToolName(name: string): boolean {
  return QUESTION_TOOL_NAMES.has(name)
}

export function normalizeQuestionnairePayload(value: unknown): PendingQuestionnaire | null {
  if (!isRecord(value)) return null

  const rawQuestions = Array.isArray(value.questions) ? value.questions : [value]
  const questions = rawQuestions.flatMap((question, index) => {
    const normalized = normalizeQuestion(question, index)
    return normalized ? [normalized] : []
  })
  if (questions.length === 0) return null

  return {
    status: 'waiting',
    interaction: 'questionnaire',
    mode: questions.length > 1 ? 'bulk' : 'single',
    questions,
  }
}

export function derivePendingQuestionnaire(events: TranscriptEvent[]): PendingQuestionnaire | null {
  let pending: PendingQuestionnaire | null = null
  for (const event of events) {
    if (event.kind === 'ask_user_question') {
      pending = normalizeQuestionnairePayload(event.payload)
      continue
    }
    if (event.kind === 'question_answered') {
      pending = null
    }
  }
  return pending
}

export function buildQuestionnaireAnswerBundle(
  questionnaire: PendingQuestionnaire,
  answersById: Record<string, string>,
): QuestionnaireAnswerBundle | null {
  const answers = questionnaire.questions.map((question) => {
    const answer = (answersById[question.id] ?? '').trim()
    return {
      id: question.id,
      header: question.header,
      question: question.question,
      answer,
    }
  })
  if (answers.some((answer) => !answer.answer)) return null
  return {
    type: 'questionnaire_answers',
    questionnaire,
    answers,
  }
}

export function buildQuestionnaireDisplayText(
  bundle: QuestionnaireAnswerBundle,
): string {
  const parts = bundle.answers.map((answer) => {
    const label = answer.header || answer.question
    return `${label}: ${answer.answer}`
  })
  return `Consultation answers: ${parts.join('; ')}`
}
